import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useChineseLang } from '../../../../i18n/chinese'
import { apiErrorText } from '../../../../i18n/apiError'
import { formatDate as formatDisplayDate, formatNumber } from '../../../../i18n/format'
import { AppUseSelector } from '../../../../redux/hooks'
import {
  churnApplyOffer,
  churnCancel,
  churnPause,
  churnSwitchPlan,
  getProfile,
} from '../../../../redux/actions/myAccountAction'
import { listMyWordsStats } from '../../../../redux/actions/wordsAction'
import { formatMoney } from '../../../../utils/currency'
import { membershipNameType } from '../../../../utils/constant'
import { captureChurnEvent } from '../../../../utils/posthogChurn'
import { ChurnOffer, ChurnReason, resolveChurnOffer, SwitchTarget } from './churnOffers'
import './churn-flow.css'

// A refusal the endpoint answered with 200, carried like an axios error so its code and message
// reach apiErrorText.
const churnRefused = (res: any) => Object.assign(new Error(res?.message), { response: { data: res } })

interface ChurnFlowModalProps {
  show: boolean
  onHide: () => void
  membershipName?: string
  /** Both spellings reach here in production; resolveChurnOffer accepts either. */
  interval?: 'monthly' | 'yearly' | 'month' | 'year' | null
  /** What the user actually pays per billing period (grandfathered amounts included). */
  amount?: number | null
  currency?: string
  saveOfferUsed: boolean
  /** A repeating discount is still running, which rules out the yearly switches. */
  repeatingDiscountActive?: boolean
  hasStripeSubscription: boolean
  /** Paid-through unix timestamp — the date access runs to after a cancel. */
  expirationUnix?: number | null
  /** Live Standard list prices in the visitor's regional currency, for switch-offer copy. */
  standardMonthlyPrice?: number | null
  standardYearlyPrice?: number | null
  plansCurrency?: string
  /** Opens the Crisp support chat. The modal stays open (on the confirm step) so the
   *  user can still cancel while chatting. */
  onContactSupport: () => void
}

type Step = 'value' | 'reason' | 'offer' | 'confirm' | 'goal' | 'done'
type DoneKind = 'cancelled' | 'discount' | 'switched' | 'paused'

// Chinese content, not interface copy: it stays as it is in every language.
const CONGRATULATIONS = '恭喜'

// Labels are translation keys.
const REASONS: { key: ChurnReason; label: string }[] = [
  { key: 'price', label: 'plan:churn.reasons.price' },
  { key: 'time', label: 'plan:churn.reasons.time' },
  { key: 'usage', label: 'plan:churn.reasons.usage' },
  { key: 'goal', label: 'plan:churn.reasons.goal' },
  { key: 'broken', label: 'plan:churn.reasons.broken' },
]

const formatDate = (unix?: number | null) => (unix ? formatDisplayDate(unix * 1000, 'long') : null)

const ChurnFlowModal: React.FC<ChurnFlowModalProps> = ({
  show,
  onHide,
  membershipName,
  interval,
  amount,
  currency = 'USD',
  saveOfferUsed,
  repeatingDiscountActive,
  hasStripeSubscription,
  expirationUnix,
  standardMonthlyPrice,
  standardYearlyPrice,
  plansCurrency,
  onContactSupport,
}) => {
  const { t } = useTranslation('plan')
  const zhLang = useChineseLang()
  const [step, setStep] = useState<Step>('value')
  const [reason, setReason] = useState<ChurnReason | null>(null)
  const [offer, setOffer] = useState<ChurnOffer | null>(null)
  const [doneKind, setDoneKind] = useState<DoneKind>('cancelled')
  const [doneDetail, setDoneDetail] = useState<string>('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [errorDetail, setErrorDetail] = useState('')

  // statsTotal is the "words you should know" population (top-11k corpus) — the
  // DENOMINATOR of the words progress bar, near-identical for every user. Words the
  // user has actually learned is everything past the New tier, same as WordsProgress.
  const wordsTotal = AppUseSelector((state: any) => {
    const total = state.words?.statsTotal || 0
    const neverSeen = state.words?.stats?.new || 0
    return Math.max(0, total - neverSeen)
  })

  const planShortName = (membershipName || '').replace('maayot ', '') || t('churn.yourPlan')
  const accessUntil = formatDate(expirationUnix)
  const analyticsProps = { plan: membershipName, interval, reason }

  useEffect(() => {
    if (!show) return
    setStep('value')
    setReason(null)
    setOffer(null)
    setError(false)
    setErrorDetail('')
    setBusy(null)
    captureChurnEvent('churn_flow_opened', { plan: membershipName, interval })
    // Freshen the step-one number; no-ops gracefully if the endpoint fails.
    listMyWordsStats({})
    // Intentionally keyed on `show` alone: the flow resets only when (re)opened.
  }, [show])

  const dismiss = () => {
    if (busy) return
    if (step !== 'done') {
      captureChurnEvent('churn_flow_dismissed', { ...analyticsProps, step })
    }
    onHide()
  }

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label)
    setError(false)
    setErrorDetail('')
    try {
      await fn()
    } catch (err: any) {
      // The endpoints answer with a specific reason ("No active subscription", "This subscription
      // is managed through the App Store or Google Play"). Swallowing it into one generic line
      // left a user with no idea why her discount was refused, and support with nothing to go on.
      const message = apiErrorText(err) ?? err?.message
      setErrorDetail(typeof message === 'string' && message !== 'Network Error' ? message : '')
      setError(true)
    } finally {
      setBusy(null)
    }
  }

  const doCancel = (from: 'offer_declined' | 'confirm' | 'goal') =>
    run('cancel', async () => {
      if (from === 'offer_declined' && offer) {
        captureChurnEvent('churn_offer_declined', { ...analyticsProps, offer: offer.kind })
      }
      const res = await churnCancel()
      if (res?.status >= 400) throw churnRefused(res)
      await getProfile().catch(() => null)
      captureChurnEvent('churn_cancelled', analyticsProps)
      setDoneKind('cancelled')
      setDoneDetail(formatDate(res?.accessUntil) || accessUntil || '')
      setStep('done')
    })

  const doAcceptDiscount = (percentOff: number) =>
    run('accept', async () => {
      const res = await churnApplyOffer()
      if (!res?.applied) throw churnRefused(res)
      await getProfile().catch(() => null)
      captureChurnEvent('churn_offer_accepted', { ...analyticsProps, offer: 'discount', percent_off: percentOff })
      setDoneKind('discount')
      setDoneDetail(String(percentOff))
      setStep('done')
    })

  const doAcceptSwitch = (target: SwitchTarget) =>
    run('accept', async () => {
      const res = await churnSwitchPlan(target)
      if (!res?.switched) throw churnRefused(res)
      await getProfile().catch(() => null)
      captureChurnEvent('churn_offer_accepted', { ...analyticsProps, offer: 'switch', target })
      setDoneKind('switched')
      setDoneDetail(target)
      setStep('done')
    })

  const doPause = (months: 1 | 2 | 3) =>
    run(`pause-${months}`, async () => {
      const res = await churnPause(months)
      if (!res?.paused) throw churnRefused(res)
      await getProfile().catch(() => null)
      captureChurnEvent('churn_offer_accepted', { ...analyticsProps, offer: 'pause', months })
      setDoneKind('paused')
      setDoneDetail(formatDate(res?.resumesAt) || '')
      setStep('done')
    })

  const selectReason = (selected: ChurnReason) => {
    setReason(selected)
    captureChurnEvent('churn_reason_selected', { plan: membershipName, interval, reason: selected })
    const resolved = resolveChurnOffer(
      { membershipName, interval, saveOfferUsed, currency, hasStripeSubscription, repeatingDiscountActive },
      selected,
    )
    if (resolved.kind === 'support') {
      captureChurnEvent('churn_offer_shown', { plan: membershipName, interval, reason: selected, offer: 'support' })
      // Opens the Crisp chat so support can fix what's broken — but the cancel path must
      // stay reachable, so the flow continues to the confirm step alongside the chat.
      onContactSupport()
      setStep('confirm')
      return
    }
    if (selected === 'goal') {
      setStep('goal')
      return
    }
    if (resolved.kind === 'none') {
      setStep('confirm')
      return
    }
    setOffer(resolved)
    captureChurnEvent('churn_offer_shown', { plan: membershipName, interval, reason: selected, offer: resolved.kind })
    setStep('offer')
  }

  // ---- step content -----------------------------------------------------------------

  const lossLine = useMemo(() => {
    if (membershipName === membershipNameType.Pro) return t('churn.loss.pro')
    if (membershipName === membershipNameType.Weekly) return t('churn.loss.weekly')
    return t('churn.loss.standard')
  }, [membershipName, t])

  const showLedger = wordsTotal > 0

  const renderValue = () => (
    <div className='churn-step' key='value'>
      <h3 className='churn-heading'>
        {showLedger ? t('churn.value.titleWithLedger') : t('churn.value.title')}
      </h3>
      {showLedger && (
        <div className='churn-ledger'>
          <div className='churn-ledger-item'>
            <span className='churn-ledger-number'>{formatNumber(wordsTotal)}</span>
            <span className='churn-ledger-label'>{t('churn.value.wordsLearned', { count: wordsTotal })}</span>
          </div>
        </div>
      )}
      <p className='churn-body'>{lossLine}</p>
      <div className='churn-actions'>
        <button className='churn-btn-primary' onClick={dismiss}>
          {t('churn.keepPlan')}
        </button>
        <button className='churn-link' onClick={() => setStep('reason')}>
          {t('churn.value.continue')}
        </button>
      </div>
    </div>
  )

  const renderReason = () => (
    <div className='churn-step' key='reason'>
      <h3 className='churn-heading'>{t('churn.reason.title')}</h3>
      <p className='churn-sub'>{t('churn.reason.sub')}</p>
      <div className='churn-reasons'>
        {REASONS.map((r) => (
          <button key={r.key} className='churn-reason-row' onClick={() => selectReason(r.key)}>
            {t(r.label)}
          </button>
        ))}
      </div>
      <div className='churn-actions'>
        <button className='churn-link' onClick={() => setStep('value')}>
          {t('common:back')}
        </button>
      </div>
    </div>
  )

  const money = (value: number, curr: string = currency) => formatMoney(value, curr)

  const renderDiscountOffer = (percentOff: number, durationMonths: number | null) => {
    const hasAmount = typeof amount === 'number' && amount > 0
    const discounted = hasAmount ? (amount as number) * (1 - percentOff / 100) : null
    // The heading states the offer rather than commenting on the reader's finances, and
    // reads off percentOff so it stays true if the discount ever changes.
    const heading = durationMonths
      ? t('churn.discount.titleMonths', { percent: percentOff, months: durationMonths })
      : t('churn.discount.titleRenewal', { percent: percentOff })
    const body = durationMonths
      ? hasAmount
        ? t('churn.discount.bodyMonthsAmount', {
            plan: planShortName,
            price: money(discounted as number),
            amount: money(amount as number),
            months: durationMonths,
          })
        : t('churn.discount.bodyMonths', { plan: planShortName, percent: percentOff, months: durationMonths })
      : hasAmount
        ? accessUntil
          ? t('churn.discount.bodyRenewalAmountOn', {
              price: money(discounted as number),
              amount: money(amount as number),
              date: accessUntil,
            })
          : t('churn.discount.bodyRenewalAmount', { price: money(discounted as number), amount: money(amount as number) })
        : accessUntil
          ? t('churn.discount.bodyRenewalOn', { percent: percentOff, date: accessUntil })
          : t('churn.discount.bodyRenewal', { percent: percentOff })
    return (
      <div className='churn-step' key='offer'>
        <h3 className='churn-heading'>{heading}</h3>
        <p className='churn-body'>{body}</p>
        <div className='churn-actions'>
          <button className='churn-btn-primary' onClick={() => doAcceptDiscount(percentOff)} disabled={!!busy}>
            {busy === 'accept'
              ? t('churn.discount.applying')
              : durationMonths
                ? t('churn.discount.claim', { percent: percentOff })
                : t('churn.discount.claimRenewal', { percent: percentOff })}
          </button>
          <button className='churn-link' onClick={() => doCancel('offer_declined')} disabled={!!busy}>
            {busy === 'cancel' ? t('churn.cancelling') : t('churn.declineAndCancel')}
          </button>
        </div>
      </div>
    )
  }

  const renderSwitchOffer = (target: SwitchTarget) => {
    // Exact prices only when the plan list is quoted in the same currency the user pays in.
    const sameCurrency = (plansCurrency || 'USD').toUpperCase() === (currency || 'USD').toUpperCase()
    let heading = ''
    let body = ''
    let cta = ''
    if (target === 'standard_yearly' && membershipName === membershipNameType.Standard) {
      heading = t('churn.switch.yearly.title')
      body =
        sameCurrency && standardYearlyPrice && amount
          ? t('churn.switch.yearly.bodyPrices', {
              from: money(amount),
              to: money(standardYearlyPrice / 12),
              yearly: money(standardYearlyPrice),
            })
          : t('churn.switch.yearly.body')
      cta = t('churn.switch.yearly.cta')
    } else if (target === 'weekly_yearly') {
      heading = t('churn.switch.weekly.title')
      body = t('churn.switch.weekly.body', { yearly: formatMoney(60, 'USD'), monthly: formatMoney(5, 'USD') })
      cta = t('churn.switch.weekly.cta')
    } else {
      // Pro → Standard, same interval
      const standardPrice = target === 'standard_monthly' ? standardMonthlyPrice : standardYearlyPrice
      heading = t('churn.switch.standard.title')
      body =
        sameCurrency && standardPrice
          ? t(target === 'standard_monthly' ? 'churn.switch.standard.bodyMonthly' : 'churn.switch.standard.bodyYearly', {
              price: money(standardPrice),
            })
          : t('churn.switch.standard.body')
      cta = t('churn.switch.standard.cta')
    }
    return (
      <div className='churn-step' key='offer'>
        <h3 className='churn-heading'>{heading}</h3>
        <p className='churn-body'>{body}</p>
        <div className='churn-actions'>
          <button className='churn-btn-primary' onClick={() => doAcceptSwitch(target)} disabled={!!busy}>
            {busy === 'accept' ? t('churn.switch.switching') : cta}
          </button>
          <button className='churn-link' onClick={() => doCancel('offer_declined')} disabled={!!busy}>
            {busy === 'cancel' ? t('churn.cancelling') : t('churn.declineAndCancel')}
          </button>
        </div>
      </div>
    )
  }

  const renderPauseOffer = () => (
    <div className='churn-step' key='offer'>
      <h3 className='churn-heading'>{t('churn.pause.title')}</h3>
      <p className='churn-body'>
        {accessUntil ? t('churn.pause.bodyOn', { date: accessUntil }) : t('churn.pause.body')}
      </p>
      <div className='churn-pause-options'>
        {([1, 2, 3] as const).map((months) => (
          <button
            key={months}
            className='churn-btn-secondary'
            onClick={() => doPause(months)}
            disabled={!!busy}
          >
            {busy === `pause-${months}` ? t('churn.pause.pausing') : t('churn.pause.option', { months })}
          </button>
        ))}
      </div>
      <div className='churn-actions'>
        <button className='churn-link' onClick={() => doCancel('offer_declined')} disabled={!!busy}>
          {busy === 'cancel' ? t('churn.cancelling') : t('churn.declineAndCancel')}
        </button>
      </div>
    </div>
  )

  const renderGoal = () => (
    <div className='churn-step' key='goal'>
      <h3 className='churn-heading'>
        <Trans t={t} i18nKey='churn.goal.title' components={[<span key='zh' lang={zhLang}>{CONGRATULATIONS}</span>]} />
      </h3>
      <p className='churn-body'>{t('churn.goal.body')}</p>
      <div className='churn-actions'>
        <button className='churn-btn-ink' onClick={() => doCancel('goal')} disabled={!!busy}>
          {busy === 'cancel' ? t('churn.cancelling') : t('churn.goal.finish')}
        </button>
        <button className='churn-link' onClick={dismiss} disabled={!!busy}>
          {t('churn.keepPlan')}
        </button>
      </div>
    </div>
  )

  const renderConfirm = () => (
    <div className='churn-step' key='confirm'>
      <h3 className='churn-heading'>{t('churn.confirm.title')}</h3>
      <p className='churn-body'>
        {accessUntil ? t('churn.confirm.bodyUntil', { date: accessUntil }) : t('churn.confirm.body')}
      </p>
      <div className='churn-actions'>
        <button className='churn-btn-ink' onClick={() => doCancel('confirm')} disabled={!!busy}>
          {busy === 'cancel' ? t('churn.cancelling') : t('churn.confirm.cta')}
        </button>
        <button className='churn-link' onClick={dismiss} disabled={!!busy}>
          {t('churn.keepPlan')}
        </button>
      </div>
    </div>
  )

  const renderDone = () => {
    let heading = ''
    let body = ''
    if (doneKind === 'cancelled') {
      heading = doneDetail ? t('churn.done.cancelledUntil', { date: doneDetail }) : t('churn.done.cancelled')
      body = t('churn.done.cancelledBody')
    } else if (doneKind === 'discount') {
      heading = t('churn.done.discount', { percent: doneDetail })
      body = t('churn.done.discountBody')
    } else if (doneKind === 'switched') {
      heading =
        doneDetail === 'weekly_yearly'
          ? t('churn.done.switchedWeekly')
          : doneDetail === 'standard_yearly'
            ? t('churn.done.switchedStandardYearly')
            : t('churn.done.switchedStandard')
      body = t('churn.done.switchedBody')
    } else {
      heading = doneDetail ? t('churn.done.pausedUntil', { date: doneDetail }) : t('churn.done.paused')
      body = t('churn.done.pausedBody')
    }
    return (
      <div className='churn-step' key='done'>
        <h3 className='churn-heading'>{heading}</h3>
        <p className='churn-body'>{body}</p>
        <div className='churn-actions'>
          <button className='churn-btn-primary' onClick={onHide}>
            {t('churn.done.back')}
          </button>
        </div>
      </div>
    )
  }

  const renderStep = () => {
    switch (step) {
      case 'value':
        return renderValue()
      case 'reason':
        return renderReason()
      case 'offer':
        if (offer?.kind === 'discount') return renderDiscountOffer(offer.percentOff, offer.durationMonths)
        if (offer?.kind === 'switch') return renderSwitchOffer(offer.target)
        if (offer?.kind === 'pause') return renderPauseOffer()
        return renderConfirm()
      case 'goal':
        return renderGoal()
      case 'confirm':
        return renderConfirm()
      case 'done':
        return renderDone()
    }
  }

  return (
    <Modal
      show={show}
      onHide={dismiss}
      centered
      className='churn-modal'
      dialogClassName='churn-modal-dialog'
      contentClassName='churn-modal-content'
      backdrop={busy ? 'static' : true}
      // The Crisp chat can be open alongside this modal (support path); without this,
      // react-bootstrap yanks focus back from the chat composer on every keystroke.
      enforceFocus={false}
      keyboard={!busy}
    >
      <Modal.Body className='churn-modal-body'>
        <button type='button' className='churn-close' onClick={dismiss} aria-label={t('common:close')} disabled={!!busy}>
          ×
        </button>
        {renderStep()}
        {error && (
          <p className='churn-error' role='alert'>
            {errorDetail
              ? t('churn.errorWithDetail', { detail: errorDetail })
              : t('churn.error')}
          </p>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default ChurnFlowModal
